import { createApp } from '@backstage/frontend-defaults';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import searchPagePlugin from '@backstage/plugin-search/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import authPlugin from '@backstage/plugin-auth';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha'
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha'
import { isGithubActionsAvailable } from '@backstage-community/plugin-github-actions'
import githubDeploysPlugin from '@backstage-community/plugin-github-deployments/alpha'
import { isGithubDeploymentsAvailable } from '@backstage-community/plugin-github-deployments'
import { UnifiedThemeProvider } from '@backstage/theme';
import { coreExtensionData, createExtensionInput, createFrontendModule, createRouteRef, PageBlueprint } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint, ThemeBlueprint } from '@backstage/plugin-app-react'
import { EntityCardBlueprint, EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha'
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid';

import jiraPlugin from '@axis-backstage/plugin-jira-dashboard/alpha'

import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';

import fastlyCompliancePlugin from '@internal/plugin-fastly-compliance';
import observabilityMetricsPlugin from '@internal/plugin-observability-metrics';
import pagerDutyPlugin from '@pagerduty/backstage-plugin';
import incidentIoPlugin from '@incident-io/backstage/alpha';
import { EntityOnCallCard, EntityIncidentCard, EntityAlertCard } from '@incident-io/backstage';

import { Tokenly } from './components/tokenly';
import { fastlyThemeLight, fastlyThemeDark } from './theme';
import { analyticsApi, scmAuthApi, scmIntegrationsApi, storageApi } from './apis';
import { SignInPage } from './components/signin/SignInPage';
import { navModule } from './modules/nav';
import { homeModule } from './modules/home';
import { searchModule } from './modules/search';

// Custom themes are now added as extensions in the features array.
// This merges custom themes with base backstage ones. In order to replace
// the backstage light and dark themes with our own, we now need to use the 'light' and 'dark' IDs to override the base ones.
const fastlyLight = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Fastly Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider
          theme={fastlyThemeLight}
          children={
            <>
              <Tokenly mode='light' />
              {children}
            </>
          }
        />
      ),
    },
  },
})

const fastlyDark = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Fastly Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider
          theme={fastlyThemeDark}
          children={
            <>
              <Tokenly mode='dark' />
              {children}
            </>
          }
        />
      ),
    },
  },
})

const signInPageExt = SignInPageBlueprint.make({
  params: {
    loader: async () => SignInPage,
  },
})

const fastlyResourceCard = EntityCardBlueprint.make({
    params: {
        loader: () => import('./components/catalog/ResourceCard').then(m => <m.ResourceCard />),
        filter: 'kind:resource',
    },
})

// incident.io cards filtered to only show on Components with type=service
const incidentOnCallCard = EntityCardBlueprint.make({
    name: 'incidentio-oncall-service',
    params: {
        loader: async () => <EntityOnCallCard />,
        filter: (entity) => entity.kind === 'Component' && entity.spec?.type === 'service',
    },
})

const incidentIncidentCard = EntityCardBlueprint.make({
    name: 'incidentio-incidents-service',
    params: {
        loader: async () => <EntityIncidentCard />,
        filter: (entity) => entity.kind === 'Component' && entity.spec?.type === 'service',
    },
})

const incidentAlertCard = EntityCardBlueprint.make({
    name: 'incidentio-alerts-service',
    params: {
        loader: async () => <EntityAlertCard />,
        filter: (entity) => entity.kind === 'Component' && entity.spec?.type === 'service',
    },
})


const catalogPluginOverride = catalogPlugin.withOverrides({
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/catalog',
        routeRef: createRouteRef({ aliasFor: 'catalog.catalogIndex' }),
        loader: () => import('./components/catalog/CatalogPage').then(m => <m.CatalogIndexPage />),
        noHeader: true,
      },
    }),
    EntityContentBlueprint.make({
      name: 'SystemDiagram',
      params: {
        path: 'diagram',
        title: 'System Diagram',
        filter: 'kind:system',
        loader: () => import('./components/catalog/SystemDiagram').then(m => <m.SystemDiagram />),
      },
    }),
    EntityContentBlueprint.make({
      name: 'Dependencies',
      params: {
        path: 'dependencies',
        title: 'Dependencies',
        filter: (entity) => entity.kind === 'Component' || entity.kind === "Resource",
        loader: () => import('./components/catalog/Dependencies').then(m => <m.Dependencies />),
      },
    }),
  ],
})

// Replaces the plugin's own GitHub Actions tab so the summary cards that used to sit on
// the overview page are shown above the workflow runs table. The cards stay first class
// extensions and are re-pointed at this content's 'cards' input from app-config, so they
// remain individually configurable. Everything else about the tab (path, and the route
// ref that keeps the run details subroute resolvable) is kept as the plugin had it.
const githubActionsPluginOverride = githubActionsPlugin.withOverrides({
  extensions: [
    EntityContentBlueprint.makeWithOverrides({
      inputs: {
        cards: createExtensionInput([
          coreExtensionData.reactElement,
          EntityContentBlueprint.dataRefs.filterFunction.optional(),
        ]),
      },
      factory(originalFactory, { inputs }) {
        const cards = inputs.cards.map(card => ({
          element: card.get(coreExtensionData.reactElement),
          filter: card.get(EntityContentBlueprint.dataRefs.filterFunction),
        }))
        return originalFactory({
          path: 'github-actions',
          title: 'GitHub Actions',
          group: 'github',
          routeRef: githubActionsPlugin.routes.entityContent,
          filter: isGithubActionsAvailable,
          loader: () => import('./components/catalog/GithubActions').then(m => <m.GithubActions cards={cards} />),
        })
      },
    }),
  ],
})

// Component types to skip for GitHub deployments. The tab is hidden for them.
const DEPLOYMENTS_COMPONENTS_SKIP = ['database', 'documentation', 'sds-kafka-cluster']

// The GitHub Deployments plugin only ships a card, so the tab that pairs with
// GitHub Actions under the 'github' tab group is defined here. It is shown for
// any Component carrying the github.com/project-slug annotation, so a service's
// deploy state is visible without hopping to its repository entity.
const githubDeploysPluginOverride = githubDeploysPlugin.withOverrides({
  extensions: [
    EntityContentBlueprint.make({
      name: 'deployments',
      params: {
        path: 'github-deployments',
        title: 'GitHub Deployments',
        group: 'github',
        filter: (entity) =>
          entity.kind === 'Component' &&
          !DEPLOYMENTS_COMPONENTS_SKIP.includes(entity.spec?.type as string) &&
          isGithubDeploymentsAvailable(entity),
        loader: () => import('./components/catalog/GithubDeployments').then(m => <m.GithubDeployments />),
      },
    }),
  ],
})

const app = createApp({
  features: [
    createFrontendModule({
      pluginId: 'app',
      extensions: [scmIntegrationsApi, scmAuthApi, storageApi, analyticsApi, fastlyLight, fastlyDark, signInPageExt, fastlyResourceCard, incidentOnCallCard, incidentIncidentCard, incidentAlertCard],
    }),
    navModule,
    homeModule,
    pagerDutyPlugin,
    incidentIoPlugin,
    catalogPluginOverride,
    techdocsPlugin,
    techDocsReportIssueAddonModule,
    techDocsMermaidAddonModule,
    apiDocsPlugin,
    scaffolderPlugin,
    userSettingsPlugin,
    catalogImportPlugin,
    searchPagePlugin,
    searchModule,
    catalogGraphPlugin,
    fastlyCompliancePlugin,
    observabilityMetricsPlugin,
    jiraPlugin,
    kubernetesPlugin,
    orgPlugin,
    githubActionsPluginOverride,
    githubDeploysPluginOverride,
    authPlugin,
  ],
  bindRoutes({ bind }) {
    bind(catalogPluginOverride.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPluginOverride.routes.catalogIndex,
    });
  },
})

export default app.createRoot();
